<template>
  <MainCard title="Meus Assistentes"
    subtitle="Crie seu assistente virtual para automatizar interações e impulsionar seus contatos de forma eficiente.">
    <div class="px-8 w-100">
      <div class="d-flex justify-space-between align-center w-100 ">
        <v-btn to="/assistentes/create" class="px-6 py-5 d-flex elevation-0" prepend-icon="mdi-plus" color="primary-60" rounded="xl">
          Novo assistente
        </v-btn>
        <div style="width:350px">
          <rb-input prepend-inner-icon="mdi-magnify" v-model="search"
            placeholder="Pesquisar" clearable></rb-input>
        </div>
      </div>
      <v-data-table ref="dataTable" :loading="loadingAssistents" :headers="headers" :items="items" :search="search"
        v-model:sort-by="sortBy"
        style="min-height: calc(100vh - 310px) !important; gap: 32px;">
          <template v-slot:loading>
            <div class="position-relative w-100 h-100">
              <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
              <rb-loader v-model='loadingAssistents' contained />
            </div>
          </template>
          <template v-slot:no-data v-if="search">
            <div class="u-empty-state">
              <div class="pa-8">
                <h3>Nenhum assistente encontrado para o termo <br> "{{ search }}".</h3>
                <p>Experimente redefinir sua pesquisa.</p>
              </div>
            </div>
          </template>
          <template v-slot:[`item.name`]="{ item }">
            <div class="text-left py-2">
              <v-avatar size="32" :color="item.img_ia ? undefined : 'primary-60'" class="mr-5">
                <img v-if="item.img_ia" :src="item.img_ia" class="avatar-image">
                <span v-else style="font-size: 16px; line-height: 24px;">{{ getInitials(item.name) }}</span>
              </v-avatar>
              <span class="text-subtitle-1  text-medium-emphasis">{{ item.name }}</span>
            </div>
          </template>
  
          <template v-slot:[`item.ultima_alteracao_data`]="{ item }">
            <div class="text-medium-emphasis text-left">
              <button @click="showLogs(item)" class="btn-ultima-alteracao">
                {{ item.ultima_alteracao_display }}
              </button>
            </div>
          </template>
  
          <template v-slot:[`item.status`]="{ value }">
            <div class="text-left" :class="{ 'color-green': value, 'color-red': !value }">
              {{ value ? 'Ativado' : 'Desativado' }}
            </div>
          </template>
  
          <template v-slot:[`item.actions`]="{ item }">
            <div class="text-start">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn class="px-6 py-5 d-flex position-relative " icon="mdi-dots-vertical" variant="text" v-bind="props">
                    <v-icon size="large">mdi-dots-vertical</v-icon>
                    <v-icon class="position-absolute alert-icon-crud" color="alert-system-dark" v-if="!item.model_status || !item.lgpd_status" size="x-small">mdi-alert-circle</v-icon>
                  </v-btn>
                </template>
                
                <v-list>
                  <v-list-item title="Editar" :to="'/assistentes/' + item.key">
                    <template v-slot:prepend>
                      <v-badge :model-value="!item.model_status || !item.lgpd_status" color="#FF8F0E" text-color="white" content="!" offset-x="-7" offset-y="-5" class="alert-badge-edit">
                        <v-icon icon="mdi-pencil-outline"></v-icon>
                      </v-badge>
                    </template>
                  </v-list-item>
  
  
                  <v-list-item v-if="item.status == 0" title="Ativar" @click="openPopUp('enable', item)">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-toggle-switch-off-outline"></v-icon>
                    </template>
                  </v-list-item>
  
                  <v-list-item v-else title="Desativar" @click="openPopUp('disable', item)">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-toggle-switch-off-outline"></v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item title="Visualizar Chat" @click="openChat(item.key)">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-message-outline"></v-icon>
                    </template>
                  </v-list-item>
  
                  <v-list-item title="Integração" :to="'/assistentes/' + item.key + '/integracao'">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-code-tags"></v-icon>
                    </template>
                  </v-list-item>
  
                  <v-list-item title="Compartilhar" @click="openPopUp('share', item)">
                    <template v-slot:prepend>
                      <v-icon icon="mdi-share-variant-outline"></v-icon>
                    </template>
                  </v-list-item>
  
                  <v-divider></v-divider>
                  <v-list-item title="Excluir" style="color: rgba(186, 26, 26, 1)" @click="openPopUp('delete', item)">
                    <template v-slot:prepend>
                      <v-icon color="rgba(186, 26, 26, 1)" icon="mdi-trash-can-outline"></v-icon>
                    </template>
                  </v-list-item>
  
                </v-list>
              </v-menu>
            </div>
          </template>
          
          <template v-slot:footer>
            <v-data-table-footer
              :items-per-page-options="[5, 10, 20, -1]"
              items-per-page-text="Itens por página"
              page-text="{0} de {1}"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
              first-icon="mdi-page-first"
              last-icon="mdi-page-last"
            ></v-data-table-footer>
          </template>
      </v-data-table>

      <rb-popup v-model="deletePopUp" @confirm="confirmAction('delete')" @cancel="cancelAction('delete')" confirm-text="Deletar"
        cancel-text="Cancelar" confirm-color="error-40" cancel-color="neutral-35" variant-cancel="outlined">
        <template #title>
          Excluir assistente
        </template>
        <template #message>
          Você tem certeza que quer excluir o assistente "{{ selectedAssistent.name }}"?
        </template>

        <template #confirm-icon>
          <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2.25 13.7501C1.8375 13.7501 1.48438 13.6032 1.19062 13.3094C0.896875 13.0157 0.75 12.6626 0.75 12.2501V2.50006H0V1.00006H3.75V0.250061H8.25V1.00006H12V2.50006H11.25V12.2501C11.25 12.6626 11.1031 13.0157 10.8094 13.3094C10.5156 13.6032 10.1625 13.7501 9.75 13.7501H2.25ZM9.75 2.50006H2.25V12.2501H9.75V2.50006ZM3.75 10.7501H5.25V4.00006H3.75V10.7501ZM6.75 10.7501H8.25V4.00006H6.75V10.7501Z"
              fill="#F8FAF9" />
          </svg>
        </template>
      </rb-popup>

      <rb-popup v-model="disablePopUp" @confirm="confirmAction('disable')" @cancel="cancelAction('disable')" confirm-text="Desativar"
        cancel-text="Cancelar" confirm-color="error-40" cancel-color="neutral-35" variant-cancel="outlined">
        <template #title>
          Desativar assistente
        </template>
        <template #message>
          Você tem certeza que quer desativar o assistente "{{ selectedAssistent.name }}"?
        </template>
      </rb-popup>

      <rb-popup v-model="enablePopUp" @confirm="confirmAction('enable')" @cancel="cancelAction('enable')" confirm-text="Habilitar"
        cancel-text="Cancelar" confirm-color="primary-60" cancel-color="neutral-35" variant-cancel="outlined">
        <template #title>
          Habilitar Assistente
        </template>
        <template #message>
          Você tem certeza que quer habilitar o assistente "{{ selectedAssistent.name }}"?
        </template>
      </rb-popup>

      <rb-popup v-model="sharePopUp" @confirm="copyLink" @auxiliar="openChat" @cancel="sharePopUp = false" :show-auxiliar="true" confirm-text="Copiar link" cancel-text="Cancelar" variant-cancel="outlined" cancel-color="neutral-35"
      auxiliar-text="Abrir chat" confirm-color="primary-60" auxiliar-color="primary-35" variant-auxiliar="text" width="460">
        <template #title>
          Compartilhe
        </template>
        <template #message>
          Copie o link e compartilhe o chat do seu assistente.
        </template>

        <template #auxiliar-icon>
          <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.5 15V1.5C0.5 1.0875 0.646875 0.734375 0.940625 0.440625C1.23438 0.146875 1.5875 0 2 0H14C14.4125 0 14.7656 0.146875 15.0594 0.440625C15.3531 0.734375 15.5 1.0875 15.5 1.5V10.5C15.5 10.9125 15.3531 11.2656 15.0594 11.5594C14.7656 11.8531 14.4125 12 14 12H3.5L0.5 15ZM2.8625 10.5H14V1.5H2V11.3438L2.8625 10.5Z"
              fill="#005C5C" />
          </svg>
        </template>
        <template #confirm-icon>
          <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.8623 12.8032C4.4498 12.8032 4.09668 12.6563 3.80293 12.3626C3.50918 12.0688 3.3623 11.7157 3.3623 11.3032V2.30322C3.3623 1.89072 3.50918 1.5376 3.80293 1.24385C4.09668 0.950098 4.4498 0.803223 4.8623 0.803223H11.6123C12.0248 0.803223 12.3779 0.950098 12.6717 1.24385C12.9654 1.5376 13.1123 1.89072 13.1123 2.30322V11.3032C13.1123 11.7157 12.9654 12.0688 12.6717 12.3626C12.3779 12.6563 12.0248 12.8032 11.6123 12.8032H4.8623ZM1.8623 15.8032C1.4498 15.8032 1.09668 15.6563 0.80293 15.3626C0.50918 15.0688 0.362305 14.7157 0.362305 14.3032V3.80322H1.8623V14.3032H10.1123V15.8032H1.8623Z"
              fill="#F8FAF9" />
          </svg>
        </template>
      </rb-popup>

      <rb-popup v-model="historicPopUp" :hide-buttons="true" :width="800" message-class="pa-0" @after-leave="clearLogs">
        <template #title>
          Histórico de alterações
        </template>
        <template #message>

          <v-table class="historicPopUpTable">
            <colgroup>
              <col width="33.33%">
              <col width="33.33%">
              <col width="33.33%">
            </colgroup>
            <thead>
              <tr>
                <th class="historicPopUpTableTitle">Alteração</th>
                <th class="historicPopUpTableTitle">Responsável</th>
                <th class="historicPopUpTableTitle">Data da alteração</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedAssistentHistoric" :key="item.id">
                <td class="historicPopUpTableText text-left">{{ formatTypeAction(item.type) }}</td>
                <td class="historicPopUpTableText text-left">{{ item.user.name }}</td>
                <td class="historicPopUpTableText text-left">{{ formatDate(item.created_at) }}</td>
              </tr>
            </tbody>
          </v-table>
        </template>
      </rb-popup>
    </div>
  </MainCard>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { request } from "@/ai_studio/utils/fetchData.js";
import { getInitials } from "@/ai_studio/utils/textFormatter";

import RbPopup from '@/ai_studio/components/common/RBPopup.vue';
import { useSnackbarStore } from '@/ai_studio/stores/snackbar';
import { useUserStore } from "@/ai_studio/stores/user";
import RbLoader from "@/ai_studio/components/common/RbLoader.vue";
import MainCard from "@/ai_studio/components/layout/MainCard.vue";

const userStore = useUserStore();
const user = computed(() => userStore.data)
const snackbar = useSnackbarStore();
const selectedAssistent = ref({});
const selectedAssistentHistoric = ref({});
const deletePopUp = ref(false);
const disablePopUp = ref(false);
const historicPopUp = ref(false);
const enablePopUp = ref(false);
const sharePopUp = ref(false);
const search = ref('');
const sortBy = ref([{ key: 'ultima_alteracao_data', order: 'desc' }]);
const loadingAssistents = ref(true);
const items = ref([]);

const headers = [
  {
    title: 'Nome do Assistente',
    align: 'left',
    key: 'name',
    sortable: true,
    width: '50%'
  },
  {
    title: 'Última Alteração',
    align: 'left',
    key: 'ultima_alteracao_data',
    sortable: true,
    width: '35%'
  },
  {
    title: 'Status', key: 'status',
    sortable: true,
    width: '10%'
  },
  {
    title: 'Ação', key: 'actions',
    align: 'right',
    sortable: false,
    width: '10%'
  },
];

const confirmMapping = {
  enable: {
    msg:'Assistente ativado.',
    func: async () => {await request(`/api/assistents/bot/${selectedAssistent.value.key}/enable`, {error:'Não foi possível habilitar o assistente.'});},
    onComplete: () => {selectedAssistent.value.status = true; updateLastModification(selectedAssistent.value.key)}
  },
  disable: {
    msg: 'Assistente desativado.',
    func: async () => {await request(`/api/assistents/bot/${selectedAssistent.value.key}/disable`, {error:'Não foi possível desabilitar o assistente.'});},
    onComplete: () => {selectedAssistent.value.status = false; updateLastModification(selectedAssistent.value.key)}
  },
  delete: {
    msg: 'Assistente deletado.',
    func:async () => {await request(`/api/assistents/bot/${selectedAssistent.value.key}/delete`, {success:'Assistente deletado.', error:'Não foi possível excluir o assistente.'});},
    onComplete: () => items.value = items.value.filter(item => item.key !== selectedAssistent.value.key)
  },
}

const cancelMapping = {
  enable: 'Ativação cancelada.',
  disable: 'Desativação cancelada.',
  delete: 'Exclusão cancelada.'
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

const actionTypeMap = {
  'CREATED': 'Criado',
  'EDITED': 'Editado',
  'ENABLED': 'Ativado',
  'DISABLED': 'Desativado'
};

const popUpMapping = {
  enable: enablePopUp,
  disable: disablePopUp,
  share: sharePopUp,
  delete: deletePopUp
}

const openPopUp = (name, assistent) =>{
  selectedAssistent.value = assistent;
  popUpMapping[name].value = true;
}

const confirmAction = async (action) => {
  try {
    if (!confirmMapping[action]) return;
    await confirmMapping[action].func();
    if (confirmMapping[action].onComplete) confirmMapping[action].onComplete();
    if (confirmMapping[action].msg) snackbar.success(confirmMapping[action].msg);
  } catch(err) {
    console.error(err);
  }
}

const cancelAction = (action) => {
  snackbar.success(cancelMapping[action]);
};

const updateLastModification = (assistentKey) => {
  const item = items.value.find(item => item.key === assistentKey);
  if (!item) return;
  
  const currentDate = new Date();
  item.ultima_alteracao = {
    data: currentDate,
    usuario: user.value?.name
  };
  item.ultima_alteracao_data = currentDate.getTime();
  item.ultima_alteracao_display = `${formatDate(currentDate)} por ${user?.value.name}`;
};

const showLogs = async (item) => {
  try{
    const datahistory = await request(`/api/assistents/bot/${item.id}/logs`, {error:'Não foi possível buscar o histórico de alterações.'});
    selectedAssistentHistoric.value = datahistory;
    historicPopUp.value = true;
  }catch (err) {
    console.error(err)
  }
}

const clearLogs = () => {
  selectedAssistentHistoric.value = {};
}

const copyLink = () => {
  navigator.clipboard.writeText(`${window.location.origin}/assistentes/${selectedAssistent.value.key}/chat?user_code=&idContato=&idRegistro=`)
  snackbar.success('Copiado para área de transferência')
}

const openChat = (assistentKey = false) => {
  if(assistentKey || selectedAssistent.value.key){
    window.open(`/assistentes/${assistentKey || selectedAssistent.value.key}/chat?user_code=&idContato=&idRegistro=`, '_blank');
  }
}

const formateDateTable = (response) => {
  return response.map(item => ({
    ...item, ultima_alteracao_data: item.ultima_alteracao ? new Date(item.ultima_alteracao.data).getTime() : 0,
    ultima_alteracao_display: item.ultima_alteracao ? `${formatDate(item.ultima_alteracao.data)} por ${item.ultima_alteracao.usuario}` : ''
  }));
};

const formatDate = (rawDate) => {
  if (!rawDate) return '';
  return dateFormatter.format(new Date(rawDate)).replace(',', ' às');
}

const formatTypeAction = (typeAction) => {
  return actionTypeMap[typeAction] || '';
}


onMounted( async () => {
  try{
    const assistents = await request('/api/assistents/bot/list', {error: 'Não foi possível carregar os assistentes'});
    loadingAssistents.value = false;
    items.value = formateDateTable(assistents);

  }catch(err){
    loadingAssistents.value = false;
    console.error(err);
  }
})

</script>

<style lang="scss" scoped>
.color-green {
  color: rgb(var(--v-theme-success-system));
}

.color-red {
  color: rgb(var(--v-theme-error-40));
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.historicPopUpTable {
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 13px;
}

.popUpTableMessage {
  padding: 0 !important;
}

.historicPopUpTableTitle,
.historicPopUpTableText {
  padding-block: 20px !important;
}

.historicPopUpTableTitle {
  font-size: 16px;
  color: rgb(var(--v-theme-neutral-35));
  font-weight: 700;
}

.historicPopUpTableText {
  color: rgb(var(--v-theme-neutral-60));
}

.alert-icon-crud{
  left: 65%;
  top: 20%;
}

.alert-badge-edit :deep(.v-badge__badge) {
  min-width: 12px;
  height: 12px;
  padding: 0 4px;
  font-size: 10px;
}

.btn-ultima-alteracao:hover {
  text-decoration: underline;
  text-align: start;
  cursor: pointer;
}

.v-data-table ::v-deep .v-divider {
  display: none;
}
</style>
